import './InteractiveEl.scss';
import {
  AriaRole,
  HTMLAttributes,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEventHandler,
  PropsWithChildren,
  Ref
} from 'react';

// `role` is the caller's because an element with an implicit role worth keeping -- a table row --
// must not be relabelled as a button, which would orphan its cells.
const buildInteractiveProps = <El extends HTMLElement>(
  role: AriaRole | undefined,
  disabled: boolean,
  onClick: MouseEventHandler<El> | undefined,
  onKeyDown: KeyboardEventHandler<El> | undefined
) => {
  if (disabled) {
    return {
      role,
      tabIndex: -1,
      'aria-disabled': true
    };
  }

  return {
    role,
    tabIndex: 0,
    onClick,
    onKeyDown: (e: KeyboardEvent<El>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.currentTarget.click();
      }
    }
  };
};

interface InteractiveElProps<El extends HTMLElement> extends PropsWithChildren, HTMLAttributes<El> {
  ref?: Ref<El>;
  disabled?: boolean;
}

// a11y friendly shorthand for interactive div elements
export const InteractiveDiv = ({
  children,
  ref,
  disabled = false,
  onClick,
  onKeyDown,
  ...divPassthroughProps
}: InteractiveElProps<HTMLDivElement>) => (
  <div
    {...divPassthroughProps}
    ref={ref}
    {...buildInteractiveProps('button', disabled, onClick, onKeyDown)}
  >
    {children}
  </div>
);

// a11y friendly shorthand for interactive span elements
export const InteractiveSpan = ({
  children,
  ref,
  disabled = false,
  onClick,
  onKeyDown,
  ...spanPassthroughProps
}: InteractiveElProps<HTMLSpanElement>) => (
  <span
    {...spanPassthroughProps}
    ref={ref}
    {...buildInteractiveProps('button', disabled, onClick, onKeyDown)}
  >
    {children}
  </span>
);

// a11y friendly shorthand for interactive table rows. The row keeps its implicit `row` role, so
// the caller supplies the accessible name -- an `aria-label` -- and any `aria-haspopup`.
export const InteractiveTr = ({
  children,
  ref,
  disabled = false,
  onClick,
  onKeyDown,
  ...trPassthroughProps
}: InteractiveElProps<HTMLTableRowElement>) => (
  <tr
    {...trPassthroughProps}
    ref={ref}
    {...buildInteractiveProps(undefined, disabled, onClick, onKeyDown)}
  >
    {children}
  </tr>
);
